import { setupOAB } from 'app/pages/flat/LawsuitsMonitor/api';
import { invalidateOabEligibility } from 'app/services/oabEligibility/invalidateOabEligibility';
import {
  confirmOab,
  getOabPending,
  parsePendingOab,
  PendingOab,
} from 'app/pages/flat/LawsuitsMonitor/api/oabConfirmation';
import { OabConfirmationSurface } from 'app/pages/flat/LawsuitsMonitor/api/trackOabConfirmedEvent';

const NOT_FOUND = 404;
// Só o 409 (ConflictException do lilsuit) significa "OAB já vinculada". No
// caminho de OAB o 422 é o validateRequestWithZod — requisição recusada, que
// não pode virar a tela de sucesso.
const ALREADY_LINKED = 409;

export const OAB_NOT_FOUND_MESSAGE = 'OAB não encontrada.';
export const OAB_GENERIC_ERROR_MESSAGE =
  'Não foi possível validar sua OAB. Tente novamente.';

export class OabValidationError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
  }
}

const responseStatusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

export async function submitOab(
  oabNumber: string,
  uf: string,
  pendingOab: PendingOab | null,
  surface: OabConfirmationSurface,
): Promise<void> {
  try {
    if (pendingOab) {
      await confirmOab(pendingOab.id, `${uf}${oabNumber}`, surface);
      return;
    }

    await setupOAB(oabNumber, uf, surface);
  } catch (error) {
    const status = responseStatusOf(error);

    // Uma OAB já vinculada à conta é o resultado desejado, não uma falha — e
    // como não passou pelo `.then` de `confirmOab`/`setupOAB`, a invalidação da
    // elegibilidade tem de sair daqui, ou o perfil e o onboarding seguem
    // convidando a cadastrar por até 10 min (o staleTime da query).
    if (status === ALREADY_LINKED) {
      invalidateOabEligibility();
      return;
    }

    throw new OabValidationError(
      status === NOT_FOUND ? OAB_NOT_FOUND_MESSAGE : OAB_GENERIC_ERROR_MESSAGE,
      status,
    );
  }
}

async function findPendingOab(): Promise<PendingOab | null> {
  try {
    return parsePendingOab(await getOabPending());
  } catch {
    return null;
  }
}

export async function confirmOrSetupOab(
  oabNumber: string,
  uf: string,
  surface: OabConfirmationSurface,
): Promise<void> {
  return submitOab(oabNumber, uf, await findPendingOab(), surface);
}
